import React, { Fragment, useState } from "react";
import {
  AnimatePresence,
  motion,
  useMotionValue,
  useReducedMotion,
  useSpring
} from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  CalendarDays,
  Check,
  CheckCircle2,
  KeyRound,
  Mail,
  MessageCircle,
  Send,
  ShieldCheck,
  Sparkles,
  LoaderCircle
} from "lucide-react";

import HeaderToMainPage from "../Universal/Header/Index";
import Footer from "../Universal/Footer/Index";
import "./Styles.css";

// Preserve the existing Formspree endpoint and CRM-facing payload keys.
const CONTACT_ENDPOINT = "https://formspree.io/f/xjgjagob";

const INITIAL_FORM = {
  fullName: "",
  companyName: "",
  roleTitle: "",
  email: "",
  intendedMarket: "",
  launchTimeline: "",
  currentPlatform: "",
  message: ""
};

const REQUEST_TYPES = [
  {
    id: "demo",
    icon: CalendarDays,
    title: "Book a demo",
    description: "A guided platform walkthrough"
  },
  {
    id: "sandbox",
    icon: KeyRound,
    title: "Sandbox access",
    description: "Ask about hands-on evaluation"
  },
  {
    id: "contact",
    icon: MessageCircle,
    title: "General inquiry",
    description: "Partnerships or other questions"
  }
];

const LAUNCH_TIMELINES = [
  ["", "Choose an estimated timeline"],
  ["0-30_days", "Within 30 days"],
  ["1-3_months", "1–3 months"],
  ["3-6_months", "3–6 months"],
  ["6-12_months", "6–12 months"],
  ["12_plus_months", "12+ months"],
  ["undecided", "Still evaluating"]
];

const AFTER_SUBMIT = [
  {
    number: "01",
    title: "We review the request",
    body: "We look at the context you share, including your goals and the kind of conversation you need."
  },
  {
    number: "02",
    title: "We reply by email",
    body: "For demo inquiries, the response can include scheduling options and questions to prepare the walkthrough."
  },
  {
    number: "03",
    title: "We focus on your priorities",
    body: "A conversation can center on your use case, product evaluation, integration needs or potential partnership."
  }
];

const FORM_LABELS = {
  fullName: "Full name",
  companyName: "Company or organization",
  roleTitle: "Role or title",
  email: "Email address",
  intendedMarket: "Intended market",
  launchTimeline: "Estimated launch timeline",
  currentPlatform: "Current platform or approach",
  message: "How can we help?"
};

const BookDemoHelper = () => {
  const reduceMotion = useReducedMotion();
  const [mode, setMode] = useState("demo");
  const [form, setForm] = useState(INITIAL_FORM);
  const [status, setStatus] = useState({ type: "idle", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [focusWithin, setFocusWithin] = useState(false);

  // Gentle depth only: nothing important depends on hover or animation.
  const pointerX = useMotionValue(0);
  const pointerY = useMotionValue(0);
  const rotateX = useSpring(pointerY, { stiffness: 115, damping: 23, mass: 0.6 });
  const rotateY = useSpring(pointerX, { stiffness: 115, damping: 23, mass: 0.6 });

  const isContact = mode === "contact";
  const needsOperatorDetails = !isContact;
  const update = (key) => (event) => {
    setForm((previous) => ({ ...previous, [key]: event.target.value }));
    if (status.type === "error") setStatus({ type: "idle", message: "" });
  };

  const onCardPointerMove = (event) => {
    if (reduceMotion || focusWithin || event.pointerType !== "mouse") return;
    const bounds = event.currentTarget.getBoundingClientRect();
    const x = (event.clientX - bounds.left) / bounds.width - 0.5;
    const y = (event.clientY - bounds.top) / bounds.height - 0.5;
    pointerX.set(x * 2.6);
    pointerY.set(-y * 1.9);
    event.currentTarget.style.setProperty("--ec6-light-x", `${(x + 0.5) * 100}%`);
    event.currentTarget.style.setProperty("--ec6-light-y", `${(y + 0.5) * 100}%`);
  };

  const resetTilt = () => {
    pointerX.set(0);
    pointerY.set(0);
  };

  const changeMode = (nextMode) => {
    setMode(nextMode);
    setStatus({ type: "idle", message: "" });
    resetTilt();
  };

  const validate = () => {
    const required = isContact
      ? ["fullName", "email", "message"]
      : ["fullName", "companyName", "roleTitle", "email", "intendedMarket", "launchTimeline"];

    for (const field of required) {
      if (!form[field].trim()) {
        return `${FORM_LABELS[field]} is required.`;
      }
    }

    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) {
      return "Please enter a valid email address.";
    }
    return null;
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;
    setStatus({ type: "idle", message: "" });

    const error = validate();
    if (error) {
      setStatus({ type: "error", message: error });
      return;
    }

    const requestType =
      mode === "demo" ? "Live Demo" : mode === "sandbox" ? "Sandbox Access" : "General Inquiry";

    const payload = {
      requestType,
      "Full Name": form.fullName.trim(),
      Company: form.companyName.trim() || "N/A",
      Role: form.roleTitle.trim() || "N/A",
      Email: form.email.trim(),
      "Intended Market": form.intendedMarket.trim() || "N/A",
      "Launch Timeline": form.launchTimeline || "N/A",
      "Current Platform": form.currentPlatform.trim() || "N/A",
      Message: form.message.trim() || "N/A",
      "Submitted At": new Date().toISOString()
    };

    try {
      setSubmitting(true);
      const response = await fetch(CONTACT_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        setStatus({
          type: "error",
          message: "Your request could not be sent. Please try again."
        });
        return;
      }

      setStatus({
        type: "success",
        message:
          mode === "demo"
            ? "Demo request sent. We'll reply by email with the next steps."
            : mode === "sandbox"
              ? "Sandbox request sent. We'll follow up about evaluation options."
              : "Your message has been sent. We'll respond by email."
      });
      setForm({ ...INITIAL_FORM });
    } catch (error) {
      console.error("Ethosino contact submission failed:", error);
      setStatus({
        type: "error",
        message: "We couldn't connect to the contact service. Check your connection and try again."
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Fragment>
      <HeaderToMainPage />

      <main className="eth-contact-v6-page" aria-label="Contact Ethosino">
        <div className="eth-contact-v6-atmosphere" aria-hidden="true">
          <div className="eth-contact-v6-atmosphere__grid" />
          <div className="eth-contact-v6-atmosphere__beam" />
          <div className="eth-contact-v6-atmosphere__orb eth-contact-v6-atmosphere__orb--one" />
          <div className="eth-contact-v6-atmosphere__orb eth-contact-v6-atmosphere__orb--two" />
          <div className="eth-contact-v6-atmosphere__orb eth-contact-v6-atmosphere__orb--three" />
        </div>

        <div className="eth-contact-v6-shell">
          <motion.header
            className="eth-contact-v6-intro"
            initial={reduceMotion ? false : { opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="eth-contact-v6-kicker">
              <span className="eth-contact-v6-kicker__dot" />
              CONTACT ETHOSINO <span className="eth-contact-v6-kicker__divider" /> OPERATOR INQUIRIES
            </div>
            <h1>
              Start the <span>conversation.</span>
            </h1>
            <p>
              Tell us what you're building or evaluating. Request a platform demo, ask about
              sandbox access, or send the team a question—all from one place.
            </p>
          </motion.header>

          <section className="eth-contact-v6-stage" id="eth-demo-v5-book" aria-label="Contact request form">
            <motion.div
              className="eth-contact-v6-floating-note eth-contact-v6-floating-note--left"
              aria-hidden="true"
              initial={reduceMotion ? false : { opacity: 0, x: -42 }}
              animate={{ opacity: 1, x: 0, y: reduceMotion ? 0 : [0, -8, 0] }}
              transition={{ duration: 5.2, repeat: reduceMotion ? 0 : Infinity, ease: "easeInOut" }}
            >
              <span className="eth-contact-v6-floating-note__icon"><Mail size={17} /></span>
              <span>DIRECT INQUIRY <strong>ONE CLEAR NEXT STEP</strong></span>
            </motion.div>
            <motion.div
              className="eth-contact-v6-floating-note eth-contact-v6-floating-note--right"
              aria-hidden="true"
              initial={reduceMotion ? false : { opacity: 0, x: 42 }}
              animate={{ opacity: 1, x: 0, y: reduceMotion ? 0 : [0, 9, 0] }}
              transition={{ duration: 5.9, repeat: reduceMotion ? 0 : Infinity, ease: "easeInOut" }}
            >
              <span className="eth-contact-v6-floating-note__icon"><Sparkles size={18} /></span>
              <span>TAILORED EVALUATION <strong>YOUR AGENDA FIRST</strong></span>
            </motion.div>

            <motion.div
              className="eth-contact-v6-card-perspective"
              initial={reduceMotion ? false : { opacity: 0, y: 65, scale: 0.966, rotateX: 5 }}
              animate={{ opacity: 1, y: 0, scale: 1, rotateX: 0 }}
              transition={{ duration: 0.95, delay: 0.12, ease: [0.16, 1, 0.3, 1] }}
            >
              <motion.div
                className="eth-contact-v6-card"
                style={reduceMotion ? undefined : { rotateX, rotateY }}
                onPointerMove={onCardPointerMove}
                onPointerLeave={resetTilt}
                onFocusCapture={() => { setFocusWithin(true); resetTilt(); }}
                onBlurCapture={(event) => {
                  if (!event.currentTarget.contains(event.relatedTarget)) setFocusWithin(false);
                }}
              >
                <div className="eth-contact-v6-card__mesh" aria-hidden="true" />
                <div className="eth-contact-v6-card__shine" aria-hidden="true" />
                <div className="eth-contact-v6-card__topline" aria-hidden="true" />

                <div className="eth-contact-v6-card__header">
                  <div className="eth-contact-v6-card__identity">
                    <span className="eth-contact-v6-card__identity-icon"><Send size={23} /></span>
                    <div>
                      <span className="eth-contact-v6-card__eyebrow">YOUR REQUEST</span>
                      <h2>How can we help?</h2>
                    </div>
                  </div>
                  <span className="eth-contact-v6-card__status"><span /> CONTACT FORM</span>
                </div>

                <div className="eth-contact-v6-mode-selector" role="group" aria-label="Choose inquiry type">
                  {REQUEST_TYPES.map(({ id, icon: Icon, title, description }) => (
                    <button
                      key={id}
                      type="button"
                      className={`eth-contact-v6-mode ${mode === id ? "is-active" : ""}`}
                      aria-pressed={mode === id}
                      onClick={() => changeMode(id)}
                    >
                      <span className="eth-contact-v6-mode__icon"><Icon size={21} /></span>
                      <span className="eth-contact-v6-mode__text">
                        <strong>{title}</strong>
                        <small>{description}</small>
                      </span>
                      {mode === id && <CheckCircle2 className="eth-contact-v6-mode__check" size={19} />}
                    </button>
                  ))}
                </div>

                <form className="eth-contact-v6-form" onSubmit={onSubmit} noValidate>
                  <div className="eth-contact-v6-form__fields">
                    <label className="eth-contact-v6-field" htmlFor="ec6-name">
                      <span>Full name <b>*</b></span>
                      <input
                        id="ec6-name"
                        name="fullName"
                        value={form.fullName}
                        onChange={update("fullName")}
                        autoComplete="name"
                        placeholder="Your name"
                        required
                      />
                    </label>
                    <label className="eth-contact-v6-field" htmlFor="ec6-email">
                      <span>Email address <b>*</b></span>
                      <input
                        id="ec6-email"
                        name="email"
                        type="email"
                        value={form.email}
                        onChange={update("email")}
                        autoComplete="email"
                        placeholder="name@company.com"
                        required
                      />
                    </label>

                    <AnimatePresence initial={false} mode="popLayout">
                      {needsOperatorDetails && (
                        <motion.div
                          key="operator-fields"
                          className="eth-contact-v6-form__extended"
                          initial={reduceMotion ? false : { opacity: 0, height: 0, y: -8 }}
                          animate={{ opacity: 1, height: "auto", y: 0 }}
                          exit={{ opacity: 0, height: 0, y: -8 }}
                          transition={{ duration: reduceMotion ? 0 : 0.32 }}
                        >
                          <label className="eth-contact-v6-field" htmlFor="ec6-company">
                            <span>Company or organization <b>*</b></span>
                            <input
                              id="ec6-company"
                              name="companyName"
                              value={form.companyName}
                              onChange={update("companyName")}
                              autoComplete="organization"
                              placeholder="Company name"
                              required
                            />
                          </label>
                          <label className="eth-contact-v6-field" htmlFor="ec6-role">
                            <span>Role / title <b>*</b></span>
                            <input
                              id="ec6-role"
                              name="roleTitle"
                              value={form.roleTitle}
                              onChange={update("roleTitle")}
                              autoComplete="organization-title"
                              placeholder="Founder, CTO, operations…"
                              required
                            />
                          </label>
                          <label className="eth-contact-v6-field" htmlFor="ec6-market">
                            <span>Intended market <b>*</b></span>
                            <input
                              id="ec6-market"
                              name="intendedMarket"
                              value={form.intendedMarket}
                              onChange={update("intendedMarket")}
                              placeholder="e.g. United States / target states"
                              required
                            />
                          </label>
                          <label className="eth-contact-v6-field" htmlFor="ec6-timeline">
                            <span>Estimated timeline <b>*</b></span>
                            <select
                              id="ec6-timeline"
                              name="launchTimeline"
                              value={form.launchTimeline}
                              onChange={update("launchTimeline")}
                              required
                            >
                              {LAUNCH_TIMELINES.map(([value, label]) => (
                                <option key={value || "none"} value={value}>{label}</option>
                              ))}
                            </select>
                          </label>
                          <label className="eth-contact-v6-field eth-contact-v6-field--full" htmlFor="ec6-platform">
                            <span>Current platform or approach <em>Optional</em></span>
                            <input
                              id="ec6-platform"
                              name="currentPlatform"
                              value={form.currentPlatform}
                              onChange={update("currentPlatform")}
                              placeholder="Pre-launch, building in-house, evaluating providers…"
                            />
                          </label>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    <label className="eth-contact-v6-field eth-contact-v6-field--full" htmlFor="ec6-message">
                      <span>
                        {isContact ? "Your message" : "What would you like to evaluate?"}
                        {isContact ? <b>*</b> : <em>Optional</em>}
                      </span>
                      <textarea
                        id="ec6-message"
                        name="message"
                        rows={5}
                        value={form.message}
                        onChange={update("message")}
                        placeholder={
                          isContact
                            ? "Tell us a little about your question or potential collaboration…"
                            : "Share your goals, key questions or specific parts of Ethosino you'd like to see…"
                        }
                        required={isContact}
                      />
                    </label>
                  </div>

                  <AnimatePresence mode="wait">
                    {status.type !== "idle" && (
                      <motion.div
                        key={`${status.type}-${status.message}`}
                        className={`eth-contact-v6-feedback eth-contact-v6-feedback--${status.type}`}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        role={status.type === "error" ? "alert" : "status"}
                        aria-live="polite"
                      >
                        {status.type === "success" ? <CheckCircle2 size={20} /> : <ShieldCheck size={20} />}
                        <span>{status.message}</span>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <div className="eth-contact-v6-form__footer">
                    <div className="eth-contact-v6-form__assurance">
                      <ShieldCheck size={20} />
                      <span>We use your details to respond to this inquiry.</span>
                    </div>
                    <motion.button
                      className="eth-contact-v6-submit"
                      type="submit"
                      disabled={submitting}
                      whileHover={reduceMotion || submitting ? undefined : { y: -3, scale: 1.012 }}
                      whileTap={reduceMotion || submitting ? undefined : { scale: 0.987 }}
                    >
                      <span className="eth-contact-v6-submit__symbol">
                        {submitting ? <LoaderCircle size={20} className="eth-contact-v6-spinning" /> : <Send size={20} />}
                      </span>
                      <strong>
                        {submitting
                          ? "Sending request…"
                          : mode === "demo"
                            ? "Request a demo"
                            : mode === "sandbox"
                              ? "Request sandbox access"
                              : "Send your message"}
                      </strong>
                      {!submitting && <ArrowRight size={21} />}
                    </motion.button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          </section>

          {/* Deliberately placed after the form; this page is for contacting us. */}
          <motion.section
            className="eth-contact-v6-after"
            aria-labelledby="eth-contact-v6-after-title"
            initial={reduceMotion ? false : { opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.16 }}
            transition={{ duration: 0.65 }}
          >
            <div className="eth-contact-v6-after__heading">
              <span className="eth-contact-v6-section-label">WHAT HAPPENS NEXT</span>
              <h2 id="eth-contact-v6-after-title">A conversation built around <span>your questions.</span></h2>
            </div>
            <div className="eth-contact-v6-after__steps">
              {AFTER_SUBMIT.map((step) => (
                <div className="eth-contact-v6-after__step" key={step.number}>
                  <span className="eth-contact-v6-after__number">{step.number}</span>
                  <h3>{step.title}</h3>
                  <p>{step.body}</p>
                </div>
              ))}
            </div>
            <div className="eth-contact-v6-preview">
              <div>
                <span className="eth-contact-v6-section-label">EXPLORE INDEPENDENTLY</span>
                <p>Prefer to look around first? The public demo is available without booking a call.</p>
              </div>
              <a href="https://demo.ethosino.com" target="_blank" rel="noopener noreferrer">
                Open the product demo <ArrowUpRight size={20} />
              </a>
            </div>
          </motion.section>
        </div>
      </main>

      <Footer />
    </Fragment>
  );
};

export default BookDemoHelper;
